// Typefaces: CSS stacks, the curated pairings the board offers, and the one
// thing about a look that NEEDS CODE — a webfont the app does not ship yet.
import type { BrandSpec, FontChoice, TypePairing } from './spec.js';

/** Families every visitor already has — never fetched, never installed. */
const SYSTEM_FAMILIES = new Set(
  [
    'system-ui',
    '-apple-system',
    'blinkmacsystemfont',
    'segoe ui',
    'helvetica',
    'helvetica neue',
    'arial',
    'georgia',
    'times new roman',
    'times',
    'ui-sans-serif',
    'ui-serif',
    'sans-serif',
    'serif',
  ].map((f) => f.toLowerCase()),
);

export function isSystemFamily(family: string): boolean {
  return SYSTEM_FAMILIES.has(family.trim().toLowerCase());
}

const SANS_TAIL = 'system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif';
const SERIF_TAIL = 'Georgia, "Times New Roman", serif';

/** A complete CSS font stack for one choice. */
export function fontStack(font: FontChoice): string {
  const tail = font.fallback === 'serif' ? SERIF_TAIL : SANS_TAIL;
  const family = font.family.trim();
  if (family === 'system-ui') return tail === SANS_TAIL ? SANS_TAIL : `system-ui, ${tail}`;
  return `"${family.replace(/"/g, '')}", ${tail}`;
}

/** Weights a role needs when the spec does not say. */
export function weightsFor(font: FontChoice, role: 'heading' | 'body'): number[] {
  return font.weights?.length ? font.weights : role === 'heading' ? [600, 700, 800] : [400, 600, 700];
}

/**
 * The type pairings the brand board offers. Each is a real, self-hostable
 * pairing (fontsource package named), so choosing one is never a dead end —
 * at worst it is one agent turn to install the package.
 */
export const TYPE_PAIRINGS: readonly { key: string; label: string; type: TypePairing }[] = [
  {
    key: 'montserrat-nunito-sans',
    label: 'Montserrat · Nunito Sans',
    type: {
      heading: { family: 'Montserrat', fontsource: '@fontsource/montserrat' },
      body: { family: 'Nunito Sans', fontsource: '@fontsource/nunito-sans' },
    },
  },
  {
    key: 'montserrat',
    label: 'Montserrat',
    type: {
      heading: { family: 'Montserrat', fontsource: '@fontsource/montserrat' },
      body: { family: 'Montserrat', fontsource: '@fontsource/montserrat' },
    },
  },
  {
    key: 'nunito',
    label: 'Nunito',
    type: {
      heading: { family: 'Nunito', fontsource: '@fontsource/nunito' },
      body: { family: 'Nunito', fontsource: '@fontsource/nunito' },
    },
  },
  {
    key: 'poppins-system',
    label: 'Poppins · system',
    type: {
      heading: { family: 'Poppins', fontsource: '@fontsource/poppins' },
      body: { family: 'system-ui' },
    },
  },
  {
    key: 'fraunces-inter',
    label: 'Fraunces · Inter',
    type: {
      heading: { family: 'Fraunces', fallback: 'serif', fontsource: '@fontsource/fraunces' },
      body: { family: 'Inter', fontsource: '@fontsource/inter' },
    },
  },
  {
    key: 'source-serif-sans',
    label: 'Source Serif · Sans',
    type: {
      heading: { family: 'Source Serif 4', fallback: 'serif', fontsource: '@fontsource/source-serif-4' },
      body: { family: 'Source Sans 3', fontsource: '@fontsource/source-sans-3' },
    },
  },
  {
    key: 'classic-system',
    label: 'Georgia · Helvetica',
    type: {
      heading: { family: 'Georgia', fallback: 'serif' },
      body: { family: 'Helvetica Neue' },
    },
  },
];

/** Stable identity of a pairing, for "is this one selected". */
export function pairingKey(type: TypePairing): string {
  return `${type.heading.family}|${type.body.family}`.toLowerCase();
}

export interface FontWork {
  /** npm package, e.g. "@fontsource/montserrat". */
  pkg: string;
  family: string;
  /** True when the package is not a dependency of the app yet. */
  install: boolean;
  /** Weight files the styles entry does not import yet. */
  importWeights: number[];
}

/**
 * What it would take for the app to actually RENDER this spec's typefaces.
 *
 * theme.css can name any family, but a webfont nothing loads renders in a
 * fallback face, silently. This compares the spec against the app's
 * package.json dependencies and its styles entry (`src/styles/index.css`) and
 * lists the packages to add and the `@fontsource/<pkg>/<weight>.css` imports
 * missing. Empty = the theme write alone is the whole apply.
 */
export function fontWork(
  spec: BrandSpec,
  app: { dependencies: Record<string, string>; stylesCss: string },
): FontWork[] {
  const byPkg = new Map<string, FontWork>();
  const roles: ['heading' | 'body', FontChoice][] = [
    ['heading', spec.type.heading],
    ['body', spec.type.body],
  ];
  for (const [role, font] of roles) {
    if (!font.fontsource || isSystemFamily(font.family)) continue;
    const pkg = font.fontsource;
    const entry = byPkg.get(pkg) ?? {
      pkg,
      family: font.family,
      install: !(pkg in app.dependencies),
      importWeights: [],
    };
    for (const w of weightsFor(font, role)) {
      const imported = new RegExp(`["']${pkg.replace(/[/-]/g, (c) => `\\${c}`)}/${w}\\.css["']`).test(
        app.stylesCss,
      );
      if (!imported && !entry.importWeights.includes(w)) entry.importWeights.push(w);
    }
    entry.importWeights.sort((a, b) => a - b);
    byPkg.set(pkg, entry);
  }
  return [...byPkg.values()].filter((w) => w.install || w.importWeights.length > 0);
}

/**
 * A Google Fonts stylesheet URL for previewing families that are not
 * installed yet (dev/Studio only — the live site self-hosts via fontsource).
 */
export function googleFontsHref(spec: BrandSpec): string | null {
  const families = new Map<string, Set<number>>();
  const add = (font: FontChoice, role: 'heading' | 'body') => {
    if (isSystemFamily(font.family)) return;
    const set = families.get(font.family) ?? new Set<number>();
    for (const w of weightsFor(font, role)) set.add(w);
    families.set(font.family, set);
  };
  add(spec.type.heading, 'heading');
  add(spec.type.body, 'body');
  if (families.size === 0) return null;
  const params = [...families.entries()]
    .map(
      ([family, weights]) =>
        `family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@${[...weights].sort((a, b) => a - b).join(';')}`,
    )
    .join('&');
  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}
